import { publish } from '@theme/ap-analytics';
import { ThemeEvents } from '@theme/events';

/**
 * AP variant-selection analytics.
 *
 * With the "require size selection" setting the PDP loads with no size pre-selected, so
 * every `variant:selected` event is an explicit customer action — a signal Shopify's
 * built-in analytics can't distinguish from theme-driven pre-selection. This module
 * turns those actions into analytics events without coupling any consumer to theme
 * internals:
 *
 * - `ap_size_selected` / `ap_option_selected` — a size (or other option) was picked.
 *   Fires for sold-out values too (`option_available: false`), which is unmet-demand data.
 * - `ap_size_prompt_shown` — add-to-bag was attempted before choosing a size.
 * - `ap_similar_styles_shown` / `ap_similar_styles_click` — the sold-out "More in
 *   [size]" row appeared / was followed (blocks/_ap-sold-out-similar-row.liquid), the
 *   recovery path for the sold-out picks `ap_size_selected` records.
 * - `ap_sold_out_enquiry_shown` / `ap_sold_out_enquiry_click` — the sold-out
 *   "Make an Enquiry" button replaced the Sold out button / was pressed
 *   (blocks/_ap-sold-out-enquiry.liquid), the second recovery path for the same picks.
 *   The conversion end of that funnel is `ap_enquiry_submitted`, published from
 *   assets/ap-basin-form.js on a successful send.
 *
 * Events go through the shared `publish()` (assets/ap-analytics.js) to
 * `Shopify.analytics.publish` (Web Pixels custom events). Only the main variant picker
 * stamps `data-option-name` on its controls, so product-card colour-swatch pickers are
 * deliberately excluded.
 */

/**
 * Coerces a DOM-carried id (dataset attributes are always strings) to the numeric
 * `product_id`/`variant_id` form the payload conventions require
 * (docs/analytics/analytics-architecture.md §3).
 * @param {string | null | undefined} value - The raw id value.
 * @returns {number | null} The numeric id, or null when absent or not a valid id.
 */
function numericId(value) {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
}

document.addEventListener(ThemeEvents.variantSelected, (event) => {
  const picker = /** @type {HTMLElement} */ (event.target);
  if (!(picker instanceof HTMLElement)) return;

  const optionValueId = /** @type {CustomEvent} */ (event).detail?.resource?.id;
  if (!optionValueId) return;

  const control = picker.querySelector(`[data-option-value-id="${CSS.escape(optionValueId)}"][data-option-name]`);
  if (!(control instanceof HTMLInputElement || control instanceof HTMLOptionElement)) return;

  const optionName = control.dataset.optionName ?? '';
  const isSizeOption = optionName.toLowerCase().includes('size');

  publish(isSizeOption ? 'ap_size_selected' : 'ap_option_selected', {
    option_name: optionName,
    option_value: control.dataset.optionDisplayValue || control.value,
    variant_id: numericId(control.dataset.variantId),
    option_available: control.dataset.optionAvailable ? control.dataset.optionAvailable === 'true' : null,
    product_id: numericId(picker.dataset.productId),
    product_url: picker.dataset.productUrl || null,
  });
});

document.addEventListener('ap:variant-selection-prompted', (event) => {
  publish('ap_size_prompt_shown', {
    product_id: numericId(/** @type {CustomEvent} */ (event).detail?.productId),
  });
});

/**
 * Maps a similar-styles CustomEvent's detail onto the published payload shape.
 * @param {Event} event - The `ap:similar-styles-*` CustomEvent.
 * @returns {Record<string, string | number | null>} The analytics payload.
 */
function similarStylesPayload(event) {
  const detail = /** @type {CustomEvent} */ (event).detail ?? {};
  return {
    product_id: numericId(detail.productId),
    product_url: detail.productUrl || null,
    size_value: detail.sizeValue || null,
    size_label: detail.sizeLabel || null,
    variant_id: numericId(detail.variantId),
    target_url: detail.targetUrl || null,
  };
}

document.addEventListener('ap:similar-styles-shown', (event) => {
  publish('ap_similar_styles_shown', similarStylesPayload(event));
});

document.addEventListener('ap:similar-styles-click', (event) => {
  publish('ap_similar_styles_click', similarStylesPayload(event));
});

/**
 * Maps a sold-out-enquiry CustomEvent's detail onto the published payload shape.
 * @param {Event} event - The `ap:sold-out-enquiry-*` CustomEvent.
 * @returns {Record<string, string | number | null>} The analytics payload.
 */
function soldOutEnquiryPayload(event) {
  const detail = /** @type {CustomEvent} */ (event).detail ?? {};
  return {
    product_id: numericId(detail.productId),
    product_url: detail.productUrl || null,
    variant_id: numericId(detail.variantId),
    size_value: detail.sizeValue || null,
    size_label: detail.sizeLabel || null,
    colour_label: detail.colourLabel || null,
  };
}

document.addEventListener('ap:sold-out-enquiry-shown', (event) => {
  publish('ap_sold_out_enquiry_shown', soldOutEnquiryPayload(event));
});

document.addEventListener('ap:sold-out-enquiry-click', (event) => {
  publish('ap_sold_out_enquiry_click', soldOutEnquiryPayload(event));
});
